import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { User as FirebaseUser } from 'firebase/auth';

import { useAuth } from '../../context/AuthContext';
import { 
  authenticateWithGoogle, 
  getGoogleRedirectResult,
  associateWebIDWithUser,
  isMobileDevice 
} from '../../services/auth/googleAuth';
import { 
  canActivateWebID, 
  activateWebID, 
  getWebIDByIdPublic 
} from '../../services/webids';
import WebIDActivationPrompt, { WebIDDisabledMessage } from './WebIDActivationPrompt';
import LoadingSpinner from '../ui/LoadingSpinner';

/**
 * @component WebIDActivationFlow
 * @description Componente que maneja el flujo completo de activación de WebID
 * Incluye verificación de estado, autenticación con Google y activación
 */

interface ActivationState {
  step: 'loading' | 'disabled' | 'prompt' | 'authenticating' | 'activating' | 'completed' | 'error';
  error?: string;
  webIdData?: any;
}

const WebIDActivationFlow: React.FC = () => {
  const { webId } = useParams<{ webId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [state, setState] = useState<ActivationState>({
    step: 'loading'
  });

  // Verificar estado inicial del WebID
  useEffect(() => {
    const checkWebIDStatus = async () => {
      if (!webId) {
        setState({ step: 'error', error: 'ID de perfil no válido' });
        return;
      }

      try {
        // Obtener datos del WebID
        const webIdData = await getWebIDByIdPublic(webId);
        
        // Verificar si está deshabilitado
        if (!webIdData.isEnabled) {
          setState({ step: 'disabled' });
          return;
        }

        // Verificar si ya está activado y completado
        if (webIdData.activationStatus === 'onboarding_completed') {
          // Redirigir a la vista pública del perfil
          navigate(`/profile/${webId}`);
          return;
        }

        // Si el usuario ya está autenticado y el WebID le pertenece
        if (user && webIdData.userId === user.uid) {
          if (webIdData.activationStatus === 'activated') {
            // Redirigir al onboarding
            navigate(`/onboarding/${webId}`);
            return;
          }
        }

        // Verificar si puede ser activado
        const canActivate = await canActivateWebID(webId);
        
        if (!canActivate.canActivate) {
          setState({ 
            step: 'error', 
            error: canActivate.reason || 'No se puede activar este perfil' 
          });
          return;
        }

        setState({ 
          step: 'prompt', 
          webIdData: canActivate.webIdData 
        });

      } catch (error) {
        console.error('Error verificando estado del WebID:', error);
        setState({ 
          step: 'error', 
          error: 'Error al verificar el perfil de emergencia' 
        });
      }
    };

    if (!authLoading) {
      checkWebIDStatus();
    }
  }, [webId, user, authLoading, navigate]);

  // Manejar resultado de redirect de Google (para móviles)
  useEffect(() => {
    const handleRedirectResult = async () => {
      if (state.step === 'authenticating') {
        try {
          const result = await getGoogleRedirectResult();
          if (result?.user) {
            await handleUserAuthenticated(result.user);
          }
        } catch (error) {
          console.error('Error procesando redirect de Google:', error);
          setState({ 
            step: 'error', 
            error: 'Error al procesar autenticación con Google' 
          });
        }
      }
    };

    handleRedirectResult();
  }, [state.step]);

  // Manejar usuario autenticado
  const handleUserAuthenticated = async (authenticatedUser: FirebaseUser) => {
    if (!webId) return;

    try {
      setState({ step: 'activating' });

      // Activar el WebID
      await activateWebID(webId, authenticatedUser.uid);

      // Asociar WebID con usuario
      await associateWebIDWithUser(authenticatedUser.uid, webId);

      // Redirigir al onboarding
      navigate(`/onboarding/${webId}`);

    } catch (error: any) {
      console.error('Error activando WebID:', error);
      setState({ 
        step: 'error', 
        error: error.message || 'Error al activar el perfil de emergencia' 
      });
    }
  };

  // Iniciar proceso de activación
  const handleActivate = async () => {
    if (!webId) return;

    // Si ya está autenticado, proceder directamente
    if (user) {
      await handleUserAuthenticated(user as unknown as FirebaseUser);
      return;
    }

    try {
      setState({ step: 'authenticating' });

      const result = await authenticateWithGoogle();
      await handleUserAuthenticated(result.user);

    } catch (error: any) {
      console.error('Error en autenticación:', error);
      
      if (error.message === 'REDIRECT_IN_PROGRESS') {
        // En móvil, el redirect está en progreso
        return;
      }

      setState({ 
        step: 'prompt', 
        error: error.message || 'Error al autenticar con Google',
        webIdData: state.webIdData
      });
    }
  };

  // Renderizar según el estado
  const renderContent = () => {
    switch (state.step) {
      case 'loading':
        return (
          <div className="min-h-screen flex items-center justify-center">
            <LoadingSpinner size="lg" message="Verificando perfil de emergencia..." />
          </div>
        );

      case 'disabled':
        return <WebIDDisabledMessage webId={webId!} />;

      case 'prompt':
        return (
          <WebIDActivationPrompt
            webId={state.webIdData}
            onActivate={handleActivate}
            loading={false}
            error={state.error}
          />
        );

      case 'authenticating':
        return (
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-blue-50">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md w-full mx-4">
              <LoadingSpinner size="lg" message="Autenticando con Google..." />
              <p className="text-gray-600 text-sm mt-4">
                {isMobileDevice() 
                  ? 'Serás redirigido a Google para autenticarte...'
                  : 'Completa la autenticación en la ventana emergente'
                }
              </p>
            </div>
          </div>
        );

      case 'activating':
        return (
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-blue-50">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md w-full mx-4">
              <LoadingSpinner size="lg" message="Activando perfil de emergencia..." />
              <p className="text-gray-600 text-sm mt-4">
                Configurando tu perfil personal...
              </p>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="bg-white rounded-lg shadow-lg p-6 text-center max-w-md w-full mx-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              
              <h1 className="text-xl font-semibold text-gray-900 mb-2">
                Error de Activación
              </h1>
              
              <p className="text-gray-600 mb-4">
                {state.error}
              </p>
              
              <button
                onClick={() => window.location.reload()}
                className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
              >
                Intentar de Nuevo
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return renderContent();
};

export default WebIDActivationFlow;
